
import React, { useState, useRef, useEffect } from "react";
import { getMonthMatrix, formatDate, isSameDay, parseDate, DayCell } from "../../utils/dateUtils";

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  disabled?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "YYYY-MM-DD",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(value || new Date());
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync viewDate when value changes
  useEffect(() => {
    if (value) setViewDate(value);
  }, [value]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const matrix = getMonthMatrix(year, month);

  const handlePrevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const handleToday = () => {
    const today = new Date();
    setViewDate(today);
    onChange(today);
    setIsOpen(false);
  };

  const handleSelect = (cell: DayCell) => {
    // Disabled logic
    if (minDate && cell.date < minDate) return;
    if (maxDate && cell.date > maxDate) return;
    
    onChange(cell.date);
    setIsOpen(false);
  };

  const displayValue = value ? formatDate(value) : "";

  return (
    <div className="date-picker-container" ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <div 
        className={`input-wrapper ${disabled ? "disabled" : ""}`} 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          border: "1px solid var(--border)", borderRadius: 12, padding: "10px 12px",
          background: disabled ? "#111" : "#0b0b0c", cursor: disabled ? "not-allowed" : "pointer"
        }}
      >
        <span style={{ color: displayValue ? "var(--text)" : "var(--muted)", fontSize: 14 }}>
          {displayValue || placeholder}
        </span>
        <span style={{ fontSize: 14, color: "var(--muted)" }}>📅</span>
      </div>

      {isOpen && !disabled && (
        <div 
          className="calendar-popup"
          style={{
            position: "absolute", top: "110%", left: 0, zIndex: 1000,
            background: "#1a1d24", border: "1px solid var(--border)", borderRadius: 12,
            padding: 16, boxShadow: "0 10px 30px rgba(0,0,0,0.5)", width: 280
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <button onClick={handlePrevMonth} style={navBtnStyle}>&lt;</button>
            <div style={{ fontWeight: "bold", color: "#fff" }}>{year}年 {month + 1}月</div>
            <button onClick={handleNextMonth} style={navBtnStyle}>&gt;</button>
          </div>

          {/* Weekday Headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 8, textAlign: "center" }}>
            {["日","一","二","三","四","五","六"].map((d, i) => (
              <div key={d} style={{ fontSize: 12, color: i===0||i===6 ? "#fbbf24" : "var(--muted)" }}>{d}</div>
            ))}
          </div>

          {/* Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {matrix.flat().map((cell) => {
              const isDisabled = (minDate && cell.date < minDate) || (maxDate && cell.date > maxDate);
              const isSelected = value && isSameDay(value, cell.date);
              
              return (
                <button
                  key={cell.dateStr}
                  onClick={() => !isDisabled && handleSelect(cell)}
                  disabled={!!isDisabled}
                  style={{
                    background: isSelected ? "#3b82f6" : "transparent",
                    color: isSelected ? "#fff" : (!cell.isCurrentMonth ? "#444" : isDisabled ? "#333" : (cell.isHoliday || (cell.isWeekend && cell.isCurrentMonth)) ? "#fbbf24" : "#ddd"),
                    border: cell.isToday ? "1px solid #3b82f6" : "none",
                    borderRadius: 6,
                    padding: 6,
                    fontSize: 12,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: !cell.isCurrentMonth ? 0.5 : 1,
                    position: "relative"
                  }}
                  title={cell.holidayName}
                >
                  {cell.day}
                  {cell.isHoliday && <div style={{position:"absolute", bottom:1, right:2, width:3, height:3, borderRadius:"50%", background:"#fbbf24"}} />}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ marginTop: 12, borderTop: "1px solid #333", paddingTop: 8, display: "flex", justifyContent: "center" }}>
            <button onClick={handleToday} style={{ background: "none", border: "none", color: "#3b82f6", fontSize: 12, cursor: "pointer" }}>
              回到今天
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const navBtnStyle = {
  background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", 
  borderRadius: 4, width: 24, height: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
};
